import { camelizeKeys } from 'humps';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { importDocumentMetadataListExcelFile } from '~/api/documentMetadata';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { FileInput } from '~/eds/src/components/FileInput/FileInput';
import { BulkImportDocumentMetadataType } from '~/enums/BulkImportDocumentMetadataType';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { FileMimeType } from '~/enums/FileMimeType';
import { useIntl } from 'react-intl';
import { SUCCESS } from '~/types/toast.types';
import Alert from '~/ui/components/Alert/Alert';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import WizardPageLayout from '~/ui/components/WizardPageLayout/WizardPageLayout';
import { parseApiError } from '~/utils/parseApiError';

const DEFAULT_ACCEPTED_FILES = {
  [FileMimeType.Xlsx]: [FileExtensionType.Xlsx],
  [FileMimeType.Xls]: [FileExtensionType.Xls],
};

function UploadStep(props) {
  const { dataTestid, clientId, moveToStep, stepCommonWizardProps } = props;
  const intl = useIntl();

  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [importFailedWithCurrentFile, setImportFailedWithCurrentFile] =
    useState(false);

  const isBulkDocumentMetadataImportFileAvailable = Boolean(selectedFile);

  const uploadDocumentMetadataList = () => {
    setIsLoading(true);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('client_id', clientId);

    importDocumentMetadataListExcelFile(formData)
      .then((response) => {
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'UploadStep.importSuccessMessage',
            defaultMessage:
              'Document Metadata List file has been imported successfully.',
            description: 'Success message when file is imported',
          }),
        );
        response = camelizeKeys(response);
        const { headers, items } = response.dataPreview;
        const summaryData = {
          previewHeaders: headers.map((header) => header.title),
          previewItems: items,
          importId: response.importId,
        };
        moveToStep(BulkImportDocumentMetadataType.Summary, summaryData);
      })
      .catch((errorResponse) => {
        handleImportSummaryApiError(errorResponse);
        setImportFailedWithCurrentFile(true);
        setIsLoading(false);
      });
  };

  const shouldActionButtonDisabled =
    importFailedWithCurrentFile ||
    !isBulkDocumentMetadataImportFileAvailable ||
    isLoading;

  const actionButton = {
    promise: uploadDocumentMetadataList,
    text: intl.formatMessage({
      id: 'UploadStep.previewButton',
      defaultMessage: 'Preview document metadata',
      description: 'Button to preview document metadata',
    }),
    disabled: shouldActionButtonDisabled,
  };

  const handleImportSummaryApiError = (errorResponse) => {
    const error = parseApiError(errorResponse);
    setAlertMessage(error.message);
    setShowAlert(true);
  };

  const selectedFileRendered = selectedFile && (
    <Text p={4} body1>
      <Text body2 isBold>
        {intl.formatMessage(
          {
            id: 'UploadStep.uploadedFileLabel',
            defaultMessage: 'Uploaded file: {fileName}',
            description:
              'Label for uploaded file. Example: fileName = "metadata.csv"',
          },
          {
            fileName: selectedFile.name,
          },
        )}
      </Text>
    </Text>
  );

  const handleSelectedFile = (files) => {
    setSelectedFile(files[0]);
    setShowAlert(false);
    setImportFailedWithCurrentFile(false);
  };

  const renderBody = () => {
    return (
      <FlexLayout
        space={4}
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
      >
        {isLoading ? (
          <h4>
            {intl.formatMessage({
              id: 'UploadStep.uploadingTitle',
              defaultMessage: 'Uploading Documents Metadata List',
              description: 'Title shown while uploading',
            })}
          </h4>
        ) : (
          <>
            <Text variant="subtitle">
              {intl.formatMessage({
                id: 'UploadStep.submitFileTitle',
                defaultMessage: 'Submit Document File',
                description: 'Title for file submission section',
              })}
            </Text>
            {isBulkDocumentMetadataImportFileAvailable && selectedFileRendered}
          </>
        )}

        {showAlert && (
          <Alert
            enableIcon
            variant="danger"
            onDismiss={() => setShowAlert(false)}
          >
            <div>{alertMessage}</div>
          </Alert>
        )}

        <ContentContainer loadingContent={{ isLoading }}>
          <FileInput
            accept={DEFAULT_ACCEPTED_FILES}
            onChange={handleSelectedFile}
            name={intl.formatMessage({
              id: 'UploadStep.fileInputName',
              defaultMessage: 'Upload Document',
              description: 'Name for file input',
            })}
          />
        </ContentContainer>
      </FlexLayout>
    );
  };

  return (
    <WizardPageLayout
      {...stepCommonWizardProps}
      currentStep={BulkImportDocumentMetadataType.Upload}
      actionButton={actionButton}
      children={<div data-testid={dataTestid}>{renderBody()}</div>}
    />
  );
}

UploadStep.propTypes = {
  clientId: PropTypes.string.isRequired,
  moveToStep: PropTypes.func.isRequired,
  stepCommonWizardProps: PropTypes.object.isRequired,
  dataTestid: PropTypes.string.isRequired,
};

export default UploadStep;
